import React from 'react';
import { useAuth } from '../../../AuthProvider';
import { PERSONAL_WORKSPACE_ID } from '../../../../utils/api';
import { useSubscription } from '../../../../context/SubscriptionContext';
import { CAN, can } from '../../../../utils/workspaceAccess';
import { workspaceCanInvite } from '../../../../utils/planLabel';
import { inviteIsOpen, peopleMetaLine } from './utils/peopleUtils';
import { usePeopleDirectory } from './hooks/usePeopleDirectory';
import { useInviteFlow } from './hooks/useInviteFlow';
import { useMemberActions } from './hooks/useMemberActions';
import { SwitchToTeam } from './components/SwitchToTeam';
import { InviteComposer } from './components/InviteComposer';
import { MemberRow } from './components/MemberRow';
import { InviteRow } from './components/InviteRow';
import { SeatPrompt } from './components/SeatPrompt';
import './SettingsPeopleSection.css';

export function SettingsPeopleSection() {
  const { workspaceId, user, workspaces } = useAuth();
  const subscription = useSubscription();
  const { startCheckout } = subscription;
  const isPersonal = !workspaceId || workspaceId === PERSONAL_WORKSPACE_ID;
  const currentRoom = workspaces.find((w) => w.id === workspaceId);
  const myRole = currentRoom?.role || (isPersonal ? 'owner' : 'user');
  // Only the owner sees Solo → Team. An invitee is already on this brain;
  // they never pick a plan.
  const soloLocked =
    can(myRole, CAN.manageBilling) &&
    !subscription.loading &&
    !workspaceCanInvite(currentRoom, subscription);

  const mayInvite = can(myRole, CAN.invitePeople) && !soloLocked;
  const mayRemove = can(myRole, CAN.removePeople);
  const mayChangeRoles = can(myRole, CAN.changeRoles);
  const mayManagePeople = can(myRole, CAN.managePeople);
  const seatQty = Number(currentRoom?.quantity) > 0 ? Number(currentRoom.quantity) : 0;
  const showSeats =
    !subscription.complimentary &&
    String(currentRoom?.plan || '').toLowerCase() === 'team' &&
    seatQty > 0;

  const { members, setMembers, pending, setPending, listStatus } = usePeopleDirectory({
    workspaceId,
    isPersonal,
    user,
    myRole,
    mayManagePeople,
  });

  const invites = useInviteFlow({
    workspaceId,
    workspaceName: currentRoom?.name,
    isPersonal,
    user,
    myRole,
    mayManagePeople,
    invitesNotWired: isPersonal,
    startCheckout,
    pending,
    setPending,
  });

  const membersApi = useMemberActions({
    workspaceId,
    isPersonal,
    user,
    myRole,
    mayManagePeople,
    setMembers,
    setActionError: invites.setActionError,
  });

  const openInvites = pending.filter(inviteIsOpen);
  const seatsUsed = members.length + openInvites.length;
  const metaLine = peopleMetaLine({
    memberCount: members.length,
    waitingCount: openInvites.length,
    showSeats,
    seatsUsed,
    seatQty,
  });

  if (soloLocked) {
    return <SwitchToTeam workspaceId={workspaceId} />;
  }

  const openInviteForm = () => {
    invites.setActionError('');
    membersApi.setConfirmRemoveId(null);
    invites.setInviteOpen(true);
  };

  return (
    <>
      <section className="settings-modal__section">
        <header className="settings-people__head">
          <div>
            <h3 className="settings-people__title">People</h3>
            <p className="settings-people__meta">
              {listStatus === 'loading' ? 'Loading…' : metaLine}
            </p>
          </div>
          {mayInvite && !isPersonal && !invites.inviteOpen && !invites.seatPrompt && (
            <button
              type="button"
              className="settings-modal__action-btn settings-modal__action-btn--primary"
              onClick={openInviteForm}
            >
              Invite person
            </button>
          )}
        </header>

        {mayInvite && invites.inviteOpen && !invites.seatPrompt && (
          <InviteComposer
            email={invites.email}
            inviteRole={invites.inviteRole}
            inviteBusy={invites.inviteBusy}
            onEmailChange={invites.setEmail}
            onRoleChange={invites.setInviteRole}
            onSubmit={invites.handleInvite}
            onCancel={() => {
              invites.setInviteOpen(false);
              invites.setActionError('');
            }}
          />
        )}

        {isPersonal && mayInvite && (
          <p className="settings-people__hint">
            Invites live on a named brain. Create one from the switcher, or wait
            for this account’s first brain to appear after Solo or Team.
          </p>
        )}
        {listStatus === 'error' && (
          <p className="settings-people__hint">
            Couldn’t load everyone. Showing you for now.
          </p>
        )}
        {invites.actionError && (
          <p className="settings-people__error">{invites.actionError}</p>
        )}

        {listStatus !== 'loading' && (
          <ul className="settings-people__list">
            {members.map((member) => (
              <MemberRow
                key={member.userId || member.id}
                member={member}
                currentUser={user}
                showEmails={mayManagePeople}
                mayChangeRoles={mayChangeRoles}
                mayRemove={mayRemove}
                busyMemberId={membersApi.busyMemberId}
                confirmRemoveId={membersApi.confirmRemoveId}
                onRoleChange={membersApi.handleRoleChange}
                onRemove={membersApi.handleRemove}
                onAskRemove={membersApi.setConfirmRemoveId}
                onKeep={() => membersApi.setConfirmRemoveId(null)}
              />
            ))}

            {mayManagePeople &&
              pending.map((invite) => (
                <InviteRow
                  key={invite.id}
                  invite={invite}
                  copiedId={invites.copiedId}
                  cancellingId={invites.cancellingId}
                  onCopy={invites.copyInviteLink}
                  onCancel={invites.handleCancelInvite}
                />
              ))}
          </ul>
        )}
      </section>

      {invites.seatPrompt && (
        <SeatPrompt
          seatPrompt={invites.seatPrompt}
          inviteBusy={invites.inviteBusy}
          onConfirm={invites.confirmAddSeat}
          onDismiss={() => {
            invites.setSeatPrompt(null);
            invites.setInviteOpen(true);
          }}
        />
      )}
    </>
  );
}

export default SettingsPeopleSection;
